import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControlLabel,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircleOutline";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useCallback, useEffect, useRef, useState } from "react";
import type { CareTask, CareTaskCategory } from "@shared/types/foyer";
import { foyerApi } from "../http/foyer";
import { PageHeader } from "./PageHeader";
import { describeError, useToast } from "./toast";
import { useTranslation } from "react-i18next";
import { useFormat } from "../i18n/format";
import { PermissionGate } from "../auth/access";

interface Props {
  premiseId: string;
  rooms: { id: string; name?: string }[];
}

const CATEGORY_KEY = {
  assistance: "serviceRequestQueue.category.assistance",
  water: "serviceRequestQueue.category.water",
  restroom: "serviceRequestQueue.category.restroom",
  pain: "serviceRequestQueue.category.pain",
  comfort: "serviceRequestQueue.category.comfort",
  other: "serviceRequestQueue.category.other",
} as const satisfies Record<CareTaskCategory, string>;

const STATUS_KEY = {
  open: "serviceRequestQueue.status.open",
  acknowledged: "serviceRequestQueue.status.acknowledged",
  resolved: "serviceRequestQueue.status.resolved",
} as const satisfies Record<CareTask["status"], string>;

const POLL_MS = 8000;

/**
 * Live staff queue of resident service requests. Polls every 8 s. Shows open +
 * acknowledged calls prominently (newest first), with Acknowledge / Resolve
 * actions; resolved calls are hidden unless "Show resolved" is on. Gated by
 * `canHandleServiceRequests` at the mount site (and on the server).
 */
export default function ServiceRequestQueue({ premiseId, rooms }: Props) {
  const toast = useToast();
  const { t } = useTranslation(["offers", "common"]);
  const fmt = useFormat();
  const [tasks, setTasks] = useState<CareTask[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [showResolved, setShowResolved] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  const roomLabel = useCallback(
    (id: string) => {
      const r = rooms.find((x) => x.id === id);
      return r?.name ? `${r.name} (${id})` : id;
    },
    [rooms]
  );

  const refresh = useCallback(async () => {
    try {
      const list = await foyerApi.listCareTasks(premiseId);
      setTasks(list);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [premiseId]);

  useEffect(() => {
    void refresh();
    timer.current = setInterval(() => {
      setNow(Date.now());
      void refresh();
    }, POLL_MS);
    return () => {
      if (timer.current) clearInterval(timer.current);
    };
  }, [refresh]);

  const act = async (task: CareTask, status: CareTask["status"]) => {
    setBusyId(task.id);
    setError(null);
    try {
      await foyerApi.setCareTaskStatus(premiseId, task.id, status);
      const room = roomLabel(task.roomId);
      toast.success(
        status === "resolved"
          ? t("serviceRequestQueue.resolved", { room })
          : status === "acknowledged"
            ? t("serviceRequestQueue.acknowledged", { room })
            : t("serviceRequestQueue.reopened", { room })
      );
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      toast.error(
        describeError(t, "updateServiceRequest", err, {
          room: roomLabel(task.roomId),
        })
      );
    } finally {
      setBusyId(null);
    }
  };

  const visible = tasks.filter((task) =>
    showResolved ? true : task.status !== "resolved"
  );
  const openCount = tasks.filter((task) => task.status === "open").length;

  return (
    <Stack spacing={2}>
      <PageHeader
        eyebrow={t("serviceRequestQueue.eyebrow")}
        title={t("serviceRequestQueue.title")}
        actions={
          <>
            {openCount > 0 && (
              <Chip
                size="small"
                color="error"
                label={t("serviceRequestQueue.openCount", { count: openCount })}
                data-testid="requests-open-count"
              />
            )}
            {loading && <CircularProgress size={16} />}
          </>
        }
      />

      {error && <Alert severity="error">{error}</Alert>}

      <Card variant="outlined">
        <CardContent>
          <Stack spacing={2}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Button
                size="small"
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={() => void refresh()}
                data-testid="requests-refresh"
              >
                {t("common:buttons.refresh")}
              </Button>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={showResolved}
                    onChange={(e) => setShowResolved(e.target.checked)}
                    data-testid="requests-show-resolved"
                  />
                }
                label={t("serviceRequestQueue.showResolved")}
              />
            </Stack>

            {visible.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {t("serviceRequestQueue.empty")}
              </Typography>
            ) : (
              <Box sx={{ overflowX: "auto" }}>
                <Table size="small" data-testid="requests-table">
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        {t("serviceRequestQueue.columns.unit")}
                      </TableCell>
                      <TableCell>
                        {t("serviceRequestQueue.columns.request")}
                      </TableCell>
                      <TableCell>
                        {t("serviceRequestQueue.columns.raised")}
                      </TableCell>
                      <TableCell>
                        {t("serviceRequestQueue.columns.status")}
                      </TableCell>
                      <TableCell align="right" />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visible.map((task) => (
                      <TableRow
                        key={task.id}
                        data-testid={`request-row-${task.id}`}
                      >
                        <TableCell>
                          <Typography
                            variant="body2"
                            fontWeight="fontWeightMedium"
                          >
                            {roomLabel(task.roomId)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Stack spacing={0.25}>
                            <Typography variant="body2">
                              {t(CATEGORY_KEY[task.category])}
                            </Typography>
                            {task.note && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {task.note}
                              </Typography>
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {fmt.relative(task.createdAt, now)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            variant={
                              task.status === "open" ? "filled" : "outlined"
                            }
                            color={
                              task.status === "open"
                                ? "error"
                                : task.status === "acknowledged"
                                  ? "warning"
                                  : "success"
                            }
                            label={t(STATUS_KEY[task.status])}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Stack
                            direction="row"
                            spacing={1}
                            justifyContent="flex-end"
                          >
                            <PermissionGate
                              resource="residents/care-requests"
                              level="edit"
                              place={{ premiseId }}
                            >
                              {task.status === "open" && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<CheckCircleIcon />}
                                  disabled={busyId === task.id}
                                  onClick={() => void act(task, "acknowledged")}
                                  data-testid={`request-ack-${task.id}`}
                                >
                                  {t("serviceRequestQueue.acknowledge")}
                                </Button>
                              )}
                              {task.status !== "resolved" && (
                                <Button
                                  size="small"
                                  variant="contained"
                                  color="success"
                                  startIcon={<DoneAllIcon />}
                                  disabled={busyId === task.id}
                                  onClick={() => void act(task, "resolved")}
                                  data-testid={`request-resolve-${task.id}`}
                                >
                                  {t("serviceRequestQueue.resolve")}
                                </Button>
                              )}
                            </PermissionGate>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
}
